/**
 * Refactored BettingZoneCanvas component
 * Interactive betting zone with visual effects and physics
 */

import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import { BettingZoneCanvasProps } from "./types";
import { COLORS, BETTING_CONFIG, ANIMATION_CONFIG } from "./constants";
import { ParticleSystem } from "./systems/ParticleSystem";
import { ProjectileSystem } from "./systems/ProjectileSystem";
import { CapturedAreaRenderer } from "./renderers/CapturedAreaRenderer";
import {
  drawZoneGradients,
  drawGhostCenterLine,
  HeatMapRenderer,
  FloatingTextRenderer,
} from "./renderers/EffectRenderers";
import { smoothPoints, clamp } from "./utils/canvasUtils";
import { useSoundEffect } from "../../../hooks/useSound";
import { SOUNDS } from "../../../utils/soundManager";

const BettingZoneCanvas: React.FC<BettingZoneCanvasProps> = ({
  orders,
  onBet,
  disabled = false,
  onScreenShake,
  victoryPhase = "idle",
  winner,
  transferProgress = 0,
  themeColors,
}) => {
  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const lastFrameTime = useRef<number>(0);
  const frameCount = useRef<number>(0);

  // Performance optimization: detect mobile and reduce quality
  const [isMobile] = useState(() => {
    return (
      typeof window !== "undefined" &&
      (window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ))
    );
  });

  // Reduce canvas resolution on mobile for better performance
  const pixelRatio = isMobile
    ? Math.min(window.devicePixelRatio, 1.5)
    : window.devicePixelRatio;

  // Store transferProgress in a ref to avoid restarting animation loop
  const transferProgressRef = useRef(transferProgress);
  useEffect(() => {
    transferProgressRef.current = transferProgress;
  }, [transferProgress]);

  // Ghost line state - initialize with positions showing Dojo Dog (right) winning
  // Store as pixel values that will be scaled based on canvas width
  // Reduce points on mobile for better performance
  const ghostLinePointCount = isMobile
    ? 20
    : ANIMATION_CONFIG.GHOST_LINE_POINTS;
  const ghostLinePoints = useRef<number[]>(Array(ghostLinePointCount).fill(0));

  // Initialize ghost line with bulge showing Dojo Dog winning
  const initializeGhostLine = useCallback((canvasWidth: number) => {
    const points = ghostLinePoints.current;
    // Create a curved line that bulges to the left, showing right side is winning
    const maxBend = canvasWidth * 0.15; // 15% of canvas width to the left
    for (let i = 0; i < points.length; i++) {
      const t = i / (points.length - 1); // 0 to 1
      // Create a bulge that's stronger in the middle
      const curve = Math.sin(t * 1.6 * Math.PI); // 0 to 1 to 0
      // Add some larger variation to make it look more organic
      const variation1 = Math.sin(t * Math.PI * 20) * canvasWidth * 0.05;
      const variation2 = Math.sin(t * Math.PI * 50) * canvasWidth * 0.1;
      points[i] = -maxBend * curve + variation1 + variation2;
    }
  }, []);

  // Systems (pass isMobile to optimize performance)
  const particleSystem = useRef(new ParticleSystem(isMobile));
  const projectileSystem = useRef(new ProjectileSystem(isMobile));
  const capturedAreaRenderer = useRef(new CapturedAreaRenderer());
  const heatMapRenderer = useRef(new HeatMapRenderer(isMobile));
  const floatingTextRenderer = useRef(new FloatingTextRenderer());

  // Order tracking
  const lastOrdersRef = useRef<Set<string>>(new Set());
  const lastUserClickRef = useRef<{ x: number; y: number } | null>(null);

  // Memoized colors for performance
  const colors = useMemo(() => COLORS, []);

  // Sound effects
  const playBetSmallSound = useSoundEffect(SOUNDS.BET_SMALL);
  const playBetMedSound = useSoundEffect(SOUNDS.BET_MED);
  const playBetLargeSound = useSoundEffect(SOUNDS.BET_LARGE);
  const playImpactSmallSound = useSoundEffect(SOUNDS.IMPACT_SMALL);
  const playImpactMedSound = useSoundEffect(SOUNDS.IMPACT_MED);
  const playImpactLargeSound = useSoundEffect(SOUNDS.IMPACT_LARGE);

  /**
   * Gets smoothed ghost line position at a given Y coordinate
   */
  const getSmoothedGhostLineX = useCallback(
    (y: number, canvasWidth: number, canvasHeight: number): number => {
      const smoothedPoints = smoothPoints(ghostLinePoints.current);
      const pointIndex = Math.floor((y / canvasHeight) * smoothedPoints.length);
      const ghostLineOffset = smoothedPoints[pointIndex] || 0;
      return canvasWidth / 2 + ghostLineOffset;
    },
    []
  );

  /**
   * Handles click/touch events for betting
   */
  const handleClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      let x =
        "touches" in e
          ? e.touches[0].clientX - rect.left
          : (e as React.MouseEvent).clientX - rect.left;
      const y =
        "touches" in e
          ? e.touches[0].clientY - rect.top
          : (e as React.MouseEvent).clientY - rect.top;

      // Get ghost line position and determine side
      const ghostLineX = getSmoothedGhostLineX(y, rect.width, rect.height);
      const side = x < rect.width / 2 ? "left" : "right";

      // Clamp click to correct side of ghost line
      if (side === "left" && x > ghostLineX) {
        x = Math.max(0, ghostLineX - 5);
      } else if (side === "right" && x < ghostLineX) {
        x = Math.min(rect.width, ghostLineX + 5);
      }

      // Calculate bet amount based on Y position
      const heightPercent = 1 - y / rect.height;
      const betAmount = Math.round(
        BETTING_CONFIG.MIN_BET +
          heightPercent * (BETTING_CONFIG.MAX_BET - BETTING_CONFIG.MIN_BET)
      );

      // Store click position for visual effects
      lastUserClickRef.current = { x: x / rect.width, y: y / rect.height };

      // Place bet
      onBet(side, betAmount);

      // Play appropriate bet sound based on bet size (at 80% volume)
      if (betAmount <= 150) {
        playBetSmallSound(0.2);
      } else if (betAmount <= 300) {
        playBetMedSound(0.2);
      } else {
        playBetLargeSound(0.2);
      }

      // Add heat point
      heatMapRenderer.current.addHeatPoint(
        x / rect.width,
        y / rect.height,
        betAmount / BETTING_CONFIG.MAX_BET,
        20 + (betAmount / BETTING_CONFIG.MAX_BET) * 30
      );

      // Trigger screen shake for large bets
      if (betAmount > BETTING_CONFIG.CLICK_THRESHOLD && onScreenShake) {
        const intensity =
          (betAmount - BETTING_CONFIG.CLICK_THRESHOLD) /
          BETTING_CONFIG.CLICK_THRESHOLD;
        onScreenShake(intensity);
      }
    },
    [
      disabled,
      onBet,
      onScreenShake,
      getSmoothedGhostLineX,
      playBetSmallSound,
      playBetMedSound,
      playBetLargeSound,
    ]
  );

  /**
   * Handles projectile impact on ghost line
   */
  const handleProjectileImpact = useCallback(
    (
      x: number,
      y: number,
      force: number,
      side: "left" | "right",
      amount: number
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const height = canvas.height;
      const pointIndex = Math.floor(
        (y / height) * ghostLinePoints.current.length
      );

      if (pointIndex >= 0 && pointIndex < ghostLinePoints.current.length) {
        // Apply force to ghost line
        ghostLinePoints.current[pointIndex] += side === "left" ? force : -force;

        // Clamp to maximum bend
        const maxBend = canvas.width * ANIMATION_CONFIG.GHOST_LINE_MAX_BEND;
        ghostLinePoints.current[pointIndex] = clamp(
          ghostLinePoints.current[pointIndex],
          -maxBend,
          maxBend
        );
      }

      // Play tiered impact sound based on bet amount at 60% volume (quieter than bets)
      if (amount <= 100) {
        playImpactSmallSound(0.1);
      } else if (amount <= 300) {
        playImpactMedSound(0.1);
      } else {
        playImpactLargeSound(0.1);
      }

      // Add explosion effect
      particleSystem.current.addExplosion(
        x,
        y,
        force * 3,
        side === "left" ? colors.left.glow : colors.right.glow
      );
    },
    [playImpactSmallSound, playImpactMedSound, playImpactLargeSound]
  );

  /**
   * Processes new orders
   */
  useEffect(() => {
    const currentOrderIds = new Set(orders.map((o) => o.id));

    orders.forEach((order) => {
      if (!lastOrdersRef.current.has(order.id)) {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const heightPercent =
          (order.amount - BETTING_CONFIG.MIN_BET) /
          (BETTING_CONFIG.MAX_BET - BETTING_CONFIG.MIN_BET);
        const y = (1 - heightPercent) * rect.height;

        const isLeft = order.side === "left";
        const sideColors = isLeft ? colors.left : colors.right;

        // Determine origin position
        const originX =
          order.isUserBet && lastUserClickRef.current
            ? lastUserClickRef.current.x * rect.width
            : isLeft
            ? rect.width * (0.05 + Math.random() * 0.35)
            : rect.width * (0.6 + Math.random() * 0.35);
        const originY =
          order.isUserBet && lastUserClickRef.current
            ? lastUserClickRef.current.y * rect.height
            : y;

        const betPower = order.amount / BETTING_CONFIG.MAX_BET;

        // Add shockwave (skip on mobile for non-user bets to improve performance)
        if (!isMobile || order.isUserBet) {
          particleSystem.current.addShockwave(
            originX,
            originY,
            30 + betPower * 70,
            order.isUserBet ? sideColors.userPrimary : sideColors.primary,
            2 + betPower * 3
          );
        }

        // Add floating text
        floatingTextRenderer.current.addText(
          originX,
          originY,
          `+$${order.amount}`,
          order.isUserBet ? sideColors.userPrimary : sideColors.primary
        );

        // Play appropriate bet sound for all bets (user and automated) at 80% volume
        if (order.amount <= 150) {
          playBetSmallSound(0.4);
        } else if (order.amount <= 300) {
          playBetMedSound(0.4);
        } else {
          playBetLargeSound(0.4);
        }

        // Calculate ghost line target
        const ghostLineX = getSmoothedGhostLineX(
          originY,
          rect.width,
          rect.height
        );

        // Add projectiles (reduce count on mobile)
        const projectileCount = isMobile
          ? order.amount > 300
            ? 2
            : 1
          : order.amount > 300
          ? 3
          : order.amount > 150
          ? 2
          : 1;
        for (let i = 0; i < projectileCount; i++) {
          setTimeout(() => {
            const yVariation = (Math.random() - 0.5) * 10;
            const finalY = originY + yVariation;

            projectileSystem.current.addProjectile({
              startX: originX,
              startY: finalY,
              targetX: ghostLineX,
              targetY: finalY,
              size:
                ANIMATION_CONFIG.PROJECTILE_BASE_SIZE +
                betPower * ANIMATION_CONFIG.PROJECTILE_SIZE_MULTIPLIER,
              color: order.isUserBet
                ? sideColors.userPrimary
                : sideColors.primary,
              trailColor: order.isUserBet
                ? sideColors.userGlow
                : sideColors.glow,
              isUserBet: order.isUserBet || false,
              amount: order.amount,
              speed:
                ANIMATION_CONFIG.PROJECTILE_BASE_SPEED +
                betPower * ANIMATION_CONFIG.PROJECTILE_SPEED_MULTIPLIER,
            });
          }, i * 80);
        }
      }
    });

    lastOrdersRef.current = currentOrderIds;
  }, [
    orders,
    colors,
    getSmoothedGhostLineX,
    playBetSmallSound,
    playBetMedSound,
    playBetLargeSound,
  ]);

  /**
   * Main animation loop
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let previousTime = 0;

    const animate = (currentTime: number) => {
      // Calculate delta time
      const deltaMs = previousTime ? currentTime - previousTime : 16.67; // Default to ~60fps on first frame
      previousTime = currentTime;

      // Frame rate throttling for mobile (target 30fps instead of 60fps)
      if (isMobile) {
        const targetFPS = 30;
        const targetFrameTime = 1000 / targetFPS;

        if (currentTime - lastFrameTime.current < targetFrameTime) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }
        lastFrameTime.current = currentTime;
      }

      // Save the context state before scaling
      ctx.save();

      // Scale the context for high DPI support
      ctx.scale(pixelRatio, pixelRatio);

      // Use the logical dimensions (not the physical pixel dimensions)
      const width = canvas.width / pixelRatio;
      const height = canvas.height / pixelRatio;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw base layers
      drawZoneGradients(
        ctx,
        width,
        height,
        undefined,
        victoryPhase,
        winner,
        transferProgressRef.current
      );
      // drawPowerGradient(ctx, width, height);
      heatMapRenderer.current.updateAndDraw(ctx, width, height);

      // Draw captured area
      capturedAreaRenderer.current.render(
        ctx,
        width,
        height,
        ghostLinePoints.current,
        victoryPhase,
        winner,
        transferProgressRef.current
      );

      // Update and draw particles (modified during victory)
      // Reduce particle spawning on mobile
      if (victoryPhase !== "idle" && winner) {
        // As soon as victory begins (meter-overdrive), only spawn particles on winner's side with increased rate
        const loserIsLeft = winner === "B";
        const winnerX = loserIsLeft ? width * 0.75 : width * 0.25;
        // Spawn more particles on winner's side (less on mobile)
        const particleCount = isMobile ? 1 : 3;
        for (let i = 0; i < particleCount; i++) {
          if (Math.random() < (isMobile ? 0.15 : 0.3)) {
            particleSystem.current.spawnParticlesAtPosition(
              winnerX + (Math.random() - 0.5) * width * 0.4,
              Math.random() * height,
              1,
              loserIsLeft ? colors.right.primary : colors.left.primary
            );
          }
        }
      } else if (!isMobile || frameCount.current % 2 === 0) {
        // Normal particle spawning only during idle (skip every other frame on mobile)
        particleSystem.current.spawnAmbientParticles(width, height);
      }
      frameCount.current++;
      particleSystem.current.updateAndDrawParticles(ctx, deltaMs);
      particleSystem.current.updateAndDrawShockwaves(ctx, deltaMs);

      // Update and draw projectiles
      projectileSystem.current.updateAndDraw(
        ctx,
        width,
        ghostLinePoints.current,
        handleProjectileImpact
      );

      // Draw ghost line
      drawGhostCenterLine(
        ctx,
        width,
        height,
        ghostLinePoints.current,
        themeColors
      );

      // Draw explosions and text
      particleSystem.current.updateAndDrawExplosions(ctx, deltaMs);
      const particleRequests = floatingTextRenderer.current.updateAndDraw(
        ctx,
        deltaMs
      );

      // Spawn particles for fading text (reduce count for better performance)
      particleRequests.forEach((request) => {
        particleSystem.current.spawnParticlesAtPosition(
          request.x,
          request.y,
          2, // Reduced from 5 to 2 particles per text
          request.color
        );
      });

      // Restore the context state
      ctx.restore();

      animationRef.current = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [handleProjectileImpact, victoryPhase, winner, colors]);

  /**
   * Handle canvas resize and initial ghost line setup
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let initialized = false;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      // Use reduced pixel ratio on mobile for better performance
      canvas.width = rect.width * pixelRatio;
      canvas.height = rect.height * pixelRatio;

      // Scale the canvas back up using CSS
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";

      // Initialize ghost line on first setup
      if (!initialized && rect.width > 0) {
        initializeGhostLine(rect.width);
        initialized = true;
      }
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [initializeGhostLine]);

  return (
    <div
      className="flex-1 relative"
      onMouseDown={handleClick}
      onTouchStart={handleClick}
      style={{ cursor: disabled ? "default" : "pointer" }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ touchAction: "none" }}
      />

      {/* Static UI elements */}
      <div
        className={`absolute top-0 bottom-0 w-px pointer-events-none ${
          themeColors?.grid?.centerLine || "bg-gray-400/15"
        }`}
        style={{ left: "50%" }}
      />

      {/* Bet amount indicators */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className={`absolute top-2 left-2 text-xs ${
            themeColors?.teamA?.text || "text-amber-400"
          } font-mono bg-black/50 px-2 py-1 rounded`}
        >
          ${BETTING_CONFIG.MAX_BET}
        </div>
        <div
          className={`absolute top-1/2 left-2 text-xs ${
            themeColors?.teamA?.text || "text-amber-400"
          } font-mono bg-black/50 px-2 py-1 rounded -translate-y-1/2`}
        >
          ${Math.round((BETTING_CONFIG.MIN_BET + BETTING_CONFIG.MAX_BET) / 2)}
        </div>
        <div
          className={`absolute bottom-2 left-2 text-xs ${
            themeColors?.teamA?.text || "text-amber-400"
          } font-mono bg-black/50 px-2 py-1 rounded`}
        >
          ${BETTING_CONFIG.MIN_BET}
        </div>

        <div
          className={`absolute top-2 right-2 text-xs ${
            themeColors?.teamB?.text || "text-purple-400"
          } font-mono bg-black/50 px-2 py-1 rounded`}
        >
          ${BETTING_CONFIG.MAX_BET}
        </div>
        <div
          className={`absolute top-1/2 right-2 text-xs ${
            themeColors?.teamB?.text || "text-purple-400"
          } font-mono bg-black/50 px-2 py-1 rounded -translate-y-1/2`}
        >
          ${Math.round((BETTING_CONFIG.MIN_BET + BETTING_CONFIG.MAX_BET) / 2)}
        </div>
        <div
          className={`absolute bottom-2 right-2 text-xs ${
            themeColors?.teamB?.text || "text-purple-400"
          } font-mono bg-black/50 px-2 py-1 rounded`}
        >
          ${BETTING_CONFIG.MIN_BET}
        </div>
      </div>
    </div>
  );
};

export default BettingZoneCanvas;
